import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Save,
  FileText,
  Plus,
  Search,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPut } from '../lib/api'

type ConfigMap = Record<string, string>

// Etiquetas amigables para claves bien conocidas. El resto cae al fallback (capitalizado).
const LABELS: Record<string, string> = {
  Texto_SobreNosotros: 'Sobre nosotros',
  Texto_FAQ: 'Preguntas frecuentes (FAQ)',
  Texto_Devoluciones: 'Política de devoluciones',
  Texto_Envios: 'Política de envíos',
  Texto_TerminosCondiciones: 'Términos y condiciones',
  Texto_Privacidad: 'Aviso de privacidad',
  Texto_Garantia: 'Garantía',
  Texto_FormaCompra: 'Formas de compra',
  Texto_FormaPago: 'Formas de pago',
  Texto_Contacto: 'Contacto',
  Texto_FooterLegal: 'Footer legal',
}

function humanize(key: string): string {
  if (LABELS[key]) return LABELS[key]
  const base = key.startsWith('Texto_') ? key.slice('Texto_'.length) : key
  return base
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function Textos() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<ConfigMap>({})
  const [filter, setFilter] = useState('')
  const [newKey, setNewKey] = useState('')
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-config', 'textos'],
    queryFn: () => apiGet<{ data: ConfigMap }>('/api/admin/config'),
  })

  // Filtrar solo claves Texto_*
  const original = useMemo<ConfigMap>(() => {
    if (!data?.data) return {}
    const out: ConfigMap = {}
    for (const k of Object.keys(data.data)) {
      if (k.startsWith('Texto_')) out[k] = data.data[k] ?? ''
    }
    return out
  }, [data])

  useEffect(() => {
    setDraft((prev) => (Object.keys(prev).length === 0 ? { ...original } : prev))
  }, [original])

  const dirtyKeys = useMemo(
    () => Object.keys(draft).filter((k) => (draft[k] ?? '') !== (original[k] ?? '')),
    [draft, original],
  )

  const visibleKeys = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const keys = Object.keys(draft).sort()
    if (!q) return keys
    return keys.filter(
      (k) => k.toLowerCase().includes(q) || humanize(k).toLowerCase().includes(q),
    )
  }, [draft, filter])

  const saveMut = useMutation({
    mutationFn: async () => {
      const changes: ConfigMap = {}
      for (const k of dirtyKeys) changes[k] = draft[k] ?? ''
      return apiPut<{ updated: number }>('/api/admin/config', { changes })
    },
    onSuccess: (r) => {
      setFlash({ tipo: 'ok', msg: `${r.updated} texto(s) guardados` })
      qc.invalidateQueries({ queryKey: ['admin-config', 'textos'] })
      setTimeout(() => setFlash(null), 3000)
    },
    onError: (e: any) => setFlash({ tipo: 'err', msg: e?.message || 'Error al guardar' }),
  })

  function addNew() {
    const raw = newKey.trim()
    if (!raw) return
    const k = raw.startsWith('Texto_') ? raw : `Texto_${raw.replace(/\s+/g, '')}`
    if (draft[k] !== undefined) {
      setFlash({ tipo: 'err', msg: 'Esa clave ya existe' })
      return
    }
    setDraft((d) => ({ ...d, [k]: '' }))
    setNewKey('')
  }

  function update(k: string, v: string) {
    setDraft((d) => ({ ...d, [k]: v }))
  }

  return (
    <>
      <Topbar
        title="Textos del sitio"
        subtitle="FAQ, sobre nosotros, devoluciones y demás contenido editable"
        actions={
          <>
            {dirtyKeys.length > 0 && <Badge variant="warning">{dirtyKeys.length} sin guardar</Badge>}
            <button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={dirtyKeys.length === 0 || saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveMut.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              Guardar cambios
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {/* Controles superiores */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar texto…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="Texto_NuevaClave"
              className="w-56 rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
            />
            <button
              type="button"
              onClick={addNew}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-medium text-ink-700 hover:bg-ink-100"
            >
              <Plus className="size-3" /> Nuevo texto
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando textos…
          </div>
        ) : visibleKeys.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink-200 bg-white p-10 text-center">
            <FileText className="mx-auto size-8 text-ink-400" />
            <h3 className="mt-3 text-sm font-semibold text-ink-900">Sin textos cargados</h3>
            <p className="mt-1 text-xs text-ink-500">
              Crea un nuevo texto con una clave del tipo <code className="font-mono">Texto_*</code>.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {visibleKeys.map((k) => {
              const isDirty = (draft[k] ?? '') !== (original[k] ?? '')
              return (
                <div key={k} className="rounded-2xl border border-ink-200 bg-white p-4">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-ink-900">{humanize(k)}</h3>
                        {isDirty && <Badge variant="warning">modificado</Badge>}
                      </div>
                      <div className="font-mono text-[11px] text-ink-500">{k}</div>
                    </div>
                  </div>
                  <textarea
                    rows={6}
                    value={draft[k] ?? ''}
                    onChange={(e) => update(k, e.target.value)}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
